// Ansicht „feature" des Pricing-Dialogs. Erklärt eine gesperrte Funktion mit
// der Copy aus dem LP-Tooltip und bietet „Paket anfragen" und „Termin buchen"
// an. Keine Preise, kein Vergleich im Dialog, „Pakete vergleichen" verlinkt
// die Preisseite.
//
// Paketnamen (Pille, aktuelles Paket) erscheinen nur bei Kunden mit
// Plan-Payment oder in der Simulation, also wenn entitlements.plan gesetzt
// ist.
import { Description, DialogTitle } from '@headlessui/react'
import { LockClosedIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { usePartnerContext, useUserContext } from '../../context'
import { useEntitlements } from '../../features/pricing/hooks'
import {
  FEATURES,
  addonPlans,
  includedIn,
  minPlan
} from '../../features/pricing/plans'
import {
  PRICING_REQUEST_KINDS,
  PRICING_REQUEST_STATUS
} from '../../features/pricing/pricing-slack'
import { describeFeatureLock } from '../../features/pricing/use-feature-gate'
import { usePricingRequest } from '../../features/pricing/use-pricing-request'
import Button from '../tailwind/Button'
import InfoCallout from '../tailwind/InfoCallout'
import {
  AdminHint,
  CompareLink,
  DialogFooter,
  DialogIconRow,
  MeetingLink,
  RequestResult,
  planDisplayName
} from './pricing-dialog-parts'

const UpgradeDialog = ({ featureKey, source, inUse, onClose }) => {
  const { t } = useTranslation()
  const entitlements = useEntitlements()
  const { partner } = usePartnerContext()
  const { isAdmin } = useUserContext()

  const feature = featureKey ? FEATURES[featureKey] : null
  const request = usePricingRequest({
    kind: PRICING_REQUEST_KINDS.FEATURE,
    key: feature ? feature.key : null,
    source
  })
  // Die Bestätigung ersetzt den Inhalt nur nach einer Anfrage aus diesem
  // Dialog. Liegt im Browser schon eine vor, startet der Hook in duplicate.
  // Dann bleibt die Erklärung stehen, nur der Button zeigt den Stand.
  const [requestedHere, setRequestedHere] = useState(false)

  if (request.isDone && requestedHere) {
    return <RequestResult status={request.status} onClose={onClose} />
  }

  const alreadyRequested = request.status === PRICING_REQUEST_STATUS.DUPLICATE

  const handleRequest = () => {
    setRequestedHere(true)
    request.send()
  }

  // Gleicher Satz wie Tooltip und UpgradeInline, relativ zum eigenen Paket.
  // Ohne Plan-Payment bleibt er neutral und erscheint hier nicht.
  const planId = entitlements.plan?.id ?? null
  const addonIn = feature ? addonPlans(feature.key) : []
  const lock = feature
    ? describeFeatureLock({
        t,
        planId,
        requiredPlan: minPlan(feature.key),
        isAddon: !!planId && addonIn.includes(planId),
        includedIn: includedIn(feature.key),
        addonIn
      })
    : null
  const availability = lock?.showPlanNames ? lock.includedText : null
  const currentPlan = planId ? planDisplayName(t, entitlements.plan) : null

  const title = feature
    ? t(`${feature.i18nKey}.title`)
    : t('pricing.dialog.generic_title')
  const description = feature
    ? t(`${feature.i18nKey}.description`)
    : t('pricing.dialog.generic_text')

  const tracking = {
    view: 'feature',
    feature: feature ? feature.key : null,
    source,
    plan: entitlements.plan?.id ?? null,
    mode: entitlements.mode
  }

  return (
    <>
      <div className='px-6 pb-6 pt-6'>
        <DialogIconRow icon={LockClosedIcon} onClose={onClose} />

        {availability && (
          <span className='mt-4 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10'>
            {availability}
          </span>
        )}

        <DialogTitle
          className={
            availability
              ? 'mt-2 text-lg font-semibold text-gray-900'
              : 'mt-4 text-lg font-semibold text-gray-900'
          }
        >
          {title}
        </DialogTitle>
        <Description className='mt-2 text-sm text-gray-600'>
          {description}
        </Description>

        {currentPlan && (
          <p className='mt-2 text-xs text-gray-500'>
            {t('pricing.dialog.current_plan', { plan: currentPlan })}
          </p>
        )}

        {(inUse || isAdmin) && (
          <div className='mt-5 space-y-3'>
            {inUse && (
              <InfoCallout tone='neutral'>
                {t('pricing.dialog.in_use_hint')}{' '}
                {t('pricing.dialog.off_allowed_hint')}
              </InfoCallout>
            )}
            {isAdmin && <AdminHint partnerId={partner?.id} />}
          </div>
        )}

        <p className='mt-5 text-xs text-gray-500'>
          {alreadyRequested
            ? t('pricing.dialog.already_sent_text')
            : t('pricing.dialog.request_hint')}
        </p>

        {request.isError && (
          <p role='alert' className='mt-2 text-sm text-red-600'>
            {t('pricing.dialog.error_text')}
          </p>
        )}
        <span className='sr-only' aria-live='polite'>
          {request.isSending ? t('pricing.dialog.sending_text') : ''}
        </span>
      </div>

      <DialogFooter>
        <CompareLink className='self-center sm:self-auto' />
        <div className='flex flex-col-reverse gap-2 sm:flex-row sm:items-center'>
          <MeetingLink tracking={tracking} />
          <Button.PrimaryLG
            text={
              alreadyRequested
                ? t('pricing.dialog.already_sent_title')
                : t('pricing.dialog.request_action')
            }
            onClick={handleRequest}
            isLoading={request.isSending}
            disabled={alreadyRequested}
          />
        </div>
      </DialogFooter>
    </>
  )
}

UpgradeDialog.propTypes = {
  featureKey: PropTypes.string,
  source: PropTypes.string,
  inUse: PropTypes.bool,
  onClose: PropTypes.func.isRequired
}

UpgradeDialog.defaultProps = {
  featureKey: null,
  source: null,
  inUse: false
}

export default UpgradeDialog
